import {PropsWithChildren, useRef, useContext, useEffect, useState} from 'react';
import Select from 'react-select';
import keyBy from 'lodash/keyBy';
import {useParams} from 'react-router-dom';
import {
  DiagramEvent,
  Set as GoSet,
  ObjectData,
  IncrementalData,
  Part,
} from 'gojs';
import {ReactDiagram} from 'gojs-react';

import {WorkspaceContext} from 'src/context';
import {
  mapGroupData,
  mapReferencedNodesToNodes,
  mapContainedNodesToNodes,
} from 'src/state/dependency2';
import {getDecomposition} from 'src/api/dependency';
import {getGroupingNodes} from 'src/api/groups';
import {
  useDiagram,
  useContextMenuPosition,
  useNode,
} from 'src/hooks';
import {
  LabeledControl,
  LoadingPlaceholder,
  NodeDetailModal,
  ContextMenu,
  MenuItem,
  Page,
  TypeLegend,
} from 'src/components';
import {
  DiagramChangeLens,
  ListNodeData,
  DiagramLink,
  DiagramNode,
} from 'src/types';
import {ColorLegendContext} from 'src/context';
import {useTypes} from 'src/components/TypeLegend/useTypes';
import {RelationshipDetailModal} from 'src/components/RelationshipDetail';
import go from 'src/util/gojs/go';

import {makeDiagramInitializer} from './diagram';
import {useComparisonJob} from './hooks';
import {CSVDownloadMenu} from './components';

import './styles.css';


const LENSES = [{
  value: 'all',
  label: 'none',
}, {
  value: 'added',
  label: 'added nodes',
}, {
  value: 'removed',
  label: 'removed nodes',
}, {
  value: 'impacted',
  label: 'impacted nodes',
}, {
  value: 'impacted-only',
  label: 'impacted only',
}] as const;

export const ComparisonPage = () => {
  const {getLegendEntry} = useContext(ColorLegendContext);
  const {currentWorkspace} = useContext(WorkspaceContext);
  const workspaceId = currentWorkspace?.id;
  const ref = useRef<ReactDiagram>(null);
  const diagram = useDiagram(ref);
  const {
    nodeTypes,
    onNodesAdded,
    linkTypes,
    onLinksAdded,
  } = useTypes();

  const {
    contextMenuState,
    showContextMenu,
  } = useContextMenuPosition();

  const [{nodes, edges}, setData] = useState<{
    nodes: Array<DiagramNode>,
    edges: Array<DiagramLink>
  }>({
    nodes: [],
    edges: [],
  });

  const {fetchNodeById: setInspectedNode, node: inspectedNode, clearData: clearInspectedNode} = useNode();
  const [inspectedEdge, setInspectedEdge] = useState<DiagramLink | undefined>();
  const [skipDiagramUpdate, setskipDiagramUpdate] = useState(false);
  const {entityId} = useParams<{entityId: string | undefined}>() ?? {};

  const [lens, setLens] = useState<DiagramChangeLens>(LENSES[4]);

  useEffect(() => {
    if (diagram) {
      diagram.model.setDataProperty(diagram.model.modelData, 'lens', lens.value);
    }
  }, [lens]);


  const {fetchComparisonJob, comparisonJob, loading} = useComparisonJob();
  const [{selectedNodeIds, selectedEdgeIds}, setSelection] = useState<{
    selectedNodeIds: Array<string>,
    selectedEdgeIds: Array<string>
  }>({
    selectedNodeIds: [],
    selectedEdgeIds: [],
  });

  const selectedNodes = nodes.filter(({id}) => selectedNodeIds.includes(id));
  const selectedEdges = edges.filter(({key: id}) => selectedEdgeIds.includes(id));

  const focusedNode = selectedNodes.length ? selectedNodes[0] : undefined;
  const focusedEdge = selectedEdges.length ? selectedEdges[0] : undefined;

  useEffect(() => {
    if (!inspectedNode) {
      return;
    }

    const preExistingNode = nodes.find(({id}) => id === inspectedNode.id);

    // If we've already fetched the node
    if (preExistingNode) {
      return;
    }

    setData({
      nodes: [...nodes, inspectedNode as unknown as DiagramNode],
      edges,
    });
  }, [inspectedNode]);

  useEffect(() => {
    if (diagram) {
      const onSelectionChanged = (event: DiagramEvent) => {
        const selection = event.subject as GoSet<Part>;
        const selectionIds = selection.filter((it) => it instanceof go.Node).map(({data}: {data: ListNodeData}) => data.id).toArray();
        const selectionKeys = selection.filter((it) => it instanceof go.Link).map(({data}: {data: ListNodeData}) => data).filter((data) => data.key !== undefined).map((data: ListNodeData) => data.key || '').toArray();
        setSelection({
          selectedNodeIds: selectionIds,
          selectedEdgeIds: selectionKeys || [],
        });
      };

      diagram.addDiagramListener('ChangedSelection', onSelectionChanged);
      return () => {
        diagram.removeDiagramListener('ChangedSelection', onSelectionChanged);
      };
    }
  }, [diagram]);

  useEffect(() => {
    (async () => {
      if (comparisonJob) {
        const diffId = comparisonJob.diffMaterializedViewDefinitionId;
        const [decomposition, groupings] = await Promise.all([
          getDecomposition(undefined, diffId),
          getGroupingNodes(null, diffId),
        ]);

        const nodes = decomposition.status === 'success' ? [
          ...mapContainedNodesToNodes(decomposition.data.containedNodes),
          ...mapReferencedNodesToNodes(decomposition.data.referencedNodes),
        ] : [];
        const groupNodes = groupings.status === 'success' ?
          mapGroupData(groupings.data || []) :
            [];

        setData({
          nodes: [...groupNodes, ...nodes],
          edges: [],
        });
      }
    })();
  }, [comparisonJob]);

  const modelChangeHandler = ({
    modifiedNodeData,
    removedNodeKeys,
    modifiedLinkData,
    removedLinkKeys,
  }: {
    insertedNodeKeys?: Array<string>,
    modifiedNodeData?: Array<DiagramNode>,
    removedNodeKeys?: Array<string>,
    insertedLinkKeys?: Array<string>,
    modifiedLinkData?: Array<DiagramLink>,
    removedLinkKeys?: Array<string>,
    modelData?: ObjectData,
  }) => {
    if (modifiedNodeData && onNodesAdded) {
      onNodesAdded(modifiedNodeData);
    }
    if (modifiedLinkData && onLinksAdded) {
      onLinksAdded(modifiedLinkData);
    }
    if (!skipDiagramUpdate) {
      setskipDiagramUpdate(true);
    }

    const idToNodeMap = keyBy(nodes, (n) => n.key);
    const idToLinkMap = keyBy(edges, (n) => n.key);

    (modifiedNodeData || []).forEach((m) => {
      idToNodeMap[m.key] = m;
    });

    (modifiedLinkData || []).forEach((m) => {
      idToLinkMap[m.key] = m;
    });

    (removedNodeKeys || []).forEach((k) => {
      delete idToNodeMap[k];
    });

    (removedLinkKeys || []).forEach((k) => {
      delete idToLinkMap[k];
    });

    setData({
      edges: Object.values(idToLinkMap),
      nodes: Object.values(idToNodeMap),
    });
  };

  const initDiagram = makeDiagramInitializer({
    onContextClick: ({x, y}) => {
      showContextMenu(x, y);
    },
    getColorTag: (item?: string | {type?: string} ) => {
      const type = !item ? undefined : typeof item === 'string' ? item : item.type;
      return getLegendEntry(type);
    },
    defaultLensValue: lens.value,
  });

  useEffect(() => {
    if (entityId && workspaceId) {
      fetchComparisonJob({id: entityId});
    }
  }, [entityId, workspaceId]);

  const hasDisplayName = !!comparisonJob?.displayName;
  const hasPr = !!comparisonJob?.pullRequestTitle;

  const comparisonContent = hasDisplayName ? (
    <span style={{display: 'block'}}>
      Comparison: {comparisonJob?.displayName}
    </span>
  ) : (
    <span>Comparison</span>
  );

  const pullRequestContent = hasPr ? (
    <span>Pull Request: {comparisonJob?.pullRequestTitle}</span>
  ) : <></>;

  const pageTitle = (
    <span>
      {comparisonContent}
      {pullRequestContent}
    </span>
  );

  return (
    <Page variant='fullscreen' title={pageTitle} controls={
      <div style={{display: 'flex', alignItems: 'flex-end', flexDirection: 'column'}}>
        {comparisonJob && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: 'row',
            paddingBottom: '1rem',
          }}>
            <LabeledControl help="Emphasize a particular aspects of the graph" layout="horizontal" label="Focus:">
              <Select<DiagramChangeLens>
                styles={{
                  container: (styles) => ({
                    ...styles,
                    width: '12rem',
                  }),
                }}
                options={LENSES}
                value={lens}
                getOptionLabel={({label}) => label}
                getOptionValue={({value}) => value}
                onChange={(lens) => !!lens && setLens(lens)}
              />
            </LabeledControl>
            <div style={{marginLeft: '0.5rem'}}>
              <CSVDownloadMenu
                leftName='left'
                rightName='right'
                jobId={comparisonJob.id}
              />
            </div>
          </div>
        )}
        <OverflowToolContainer>
          <TypeLegend nodeTypes={nodeTypes} linkTypes={linkTypes}/>
        </OverflowToolContainer>
      </div>
    }>

      {loading ? <LoadingPlaceholder position="absolute" scale={1.2} /> : (
        <>
          <ReactDiagram
            ref={ref}
            divClassName="JobDiagram"
            initDiagram={initDiagram}
            onModelChange={modelChangeHandler as (e: IncrementalData) => void}
            nodeDataArray={nodes}
            linkDataArray={edges}
            skipsDiagramUpdate={skipDiagramUpdate}
          />
          <ContextMenu
            {...contextMenuState}
          >
            {focusedNode && <MenuItem context={focusedNode} onClick={(node) => {
              if (node) {
                setInspectedNode(node.id);
              }
            }} >Item Details</MenuItem>}
            {focusedEdge && focusedEdge.from && focusedEdge.to && focusedEdge.type &&
              <MenuItem context={focusedEdge} onClick={(edge) => {
                if (edge) {
                  setInspectedEdge(edge);
                }
              }} >Relationship Details</MenuItem>}
          </ContextMenu>
          <NodeDetailModal
            isOpen={!!inspectedNode}
            toggle={clearInspectedNode}
            nodeId={inspectedNode?.id}
            goToNode={setInspectedNode}
          />
          { inspectedEdge &&
            <RelationshipDetailModal
              isOpen={!!inspectedEdge}
              toggle={() => setInspectedEdge(undefined)}
              to={inspectedEdge.to}
              from={inspectedEdge.from}
              type={inspectedEdge.type}
            />
          }
        </>
    )}
    </Page>
  );
};

const OverflowToolContainer = ({children}: PropsWithChildren) => (
  <div
    style={{
      maxHeight: '1.875rem',
      overflowY: 'visible',
    }}
  >
    <div
      style={{
        padding: '0.5rem 1rem 0.5rem',
        backgroundColor: 'rgba(255, 255, 255, 0.90)',
      }}>
      {children}
    </div>
  </div>
);
